import type { Lottery } from 'api/lotteries';
import { ELotteryPhase } from '../constants';
import { hasDrawnWinners } from './prizes';

export const getLotteryPhase = (lottery: Lottery, now: number) => {
  // SS may draw it before the end date: it takes no tickets from then on
  if (lottery.finishedAt) {
    return hasDrawnWinners(lottery)
      ? ELotteryPhase.ENDED
      : ELotteryPhase.DRAWING;
  }
  if (now < Date.parse(lottery.startAt)) return ELotteryPhase.COMING_SOON;
  if (now < Date.parse(lottery.endAt)) return ELotteryPhase.STARTED;

  // the draw may take a while after the end
  return ELotteryPhase.DRAWING;
};

/** the closest start or end still ahead, to switch the page without a reload */
// setTimeout fires at once past ~24.8 days
const MAX_TIMEOUT = 2 ** 31 - 1;

export const setTimeoutAt = (time: number, callback: () => void) =>
  setTimeout(callback, Math.min(time - Date.now(), MAX_TIMEOUT));

export const getNextPhaseChange = (lotteries: Lottery[], now: number) =>
  lotteries
    .flatMap(({ startAt, endAt }) => [Date.parse(startAt), Date.parse(endAt)])
    .filter((time) => time > now)
    .reduce<number | null>(
      (closest, time) => (closest === null || time < closest ? time : closest),
      null
    );

const PHASE_PRIORITY: Record<ELotteryPhase, number> = {
  [ELotteryPhase.STARTED]: 0,
  [ELotteryPhase.COMING_SOON]: 1,
  [ELotteryPhase.DRAWING]: 2,
  [ELotteryPhase.ENDED]: 3,
};

/** skips ENDED: SS lists keep only some drawn lotteries, the CMS gives the past one */
export const pickActiveLottery = (lotteries: Lottery[], now: number) =>
  lotteries
    .map((lottery) => ({ lottery, phase: getLotteryPhase(lottery, now) }))
    .filter(({ phase }) => phase !== ELotteryPhase.ENDED)
    .sort((a, b) => {
      const byPhase = PHASE_PRIORITY[a.phase] - PHASE_PRIORITY[b.phase];

      if (byPhase) return byPhase;

      return a.phase === ELotteryPhase.COMING_SOON
        ? Date.parse(a.lottery.startAt) - Date.parse(b.lottery.startAt)
        : Date.parse(b.lottery.endAt) - Date.parse(a.lottery.endAt);
    })[0] ?? null;
